import { useEffect, useRef } from 'react'
import { Check } from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'

export interface WizardStep {
  label: string
  status: 'pending' | 'active' | 'completed'
}

export interface WizardStepIndicatorProps {
  steps: WizardStep[]
  onNavigate?: (stepIndex: number) => void
  className?: string
}

export function WizardStepIndicator({
  steps,
  onNavigate,
  className,
}: WizardStepIndicatorProps) {
  const activeRef = useRef<HTMLButtonElement>(null)
  const activeIndex = steps.findIndex((s) => s.status === 'active')

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [activeIndex])

  return (
    <div
      className={cn(
        'flex-shrink-0 bg-background pt-14 md:pt-6 pb-2 overflow-x-auto',
        className,
      )}
    >
      <div className="flex items-start justify-center px-4 md:px-8 mx-auto max-w-screen-2xl w-full">
        {steps.map((step, idx) => {
          const isActive = step.status === 'active'
          const isCompleted = step.status === 'completed'
          const canNavigate = isCompleted && !!onNavigate
          const isLast = idx === steps.length - 1

          return (
            <div key={`${step.label}-${idx}`} className="flex items-start">
              <button
                ref={isActive ? activeRef : undefined}
                type="button"
                disabled={!canNavigate}
                onClick={canNavigate ? () => onNavigate(idx) : undefined}
                aria-current={isActive ? 'step' : undefined}
                aria-disabled={!canNavigate ? 'true' : undefined}
                aria-label={`Step ${idx + 1}: ${step.label}`}
                className={cn(
                  'flex flex-col items-center gap-[5px] transition-colors w-[100px]',
                  canNavigate
                    ? 'cursor-pointer hover:opacity-80'
                    : 'cursor-default',
                )}
              >
                <div className="relative flex h-7 w-7 items-center justify-center">
                  {isActive && (
                    <div className="absolute inset-0 rounded-full border border-[hsl(var(--step-active-ring))]" />
                  )}
                  <div
                    className={cn(
                      'relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-semibold transition-colors',
                      isCompleted
                        ? 'bg-[hsl(var(--step-completed-bg))] text-[hsl(var(--step-completed-fg))]'
                        : isActive
                          ? 'bg-[hsl(var(--step-active-bg))] text-[hsl(var(--step-active-fg))]'
                          : 'bg-[hsl(var(--step-pending-bg))] text-[hsl(var(--step-pending-fg))]',
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-3 w-3" strokeWidth={2.5} />
                    ) : (
                      idx + 1
                    )}
                  </div>
                </div>
                <span
                  className={cn(
                    'whitespace-nowrap text-sm font-normal leading-snug',
                    isActive
                      ? 'text-[hsl(var(--step-active-label))]'
                      : isCompleted
                        ? 'text-[hsl(var(--step-completed-label))]'
                        : 'text-[hsl(var(--step-pending-label))]',
                  )}
                >
                  {step.label}
                </span>
              </button>

              {!isLast && (
                <div className="flex-1 max-w-[160px] min-w-[48px] shrink pt-[13px]">
                  <div
                    className={cn(
                      'h-px w-full',
                      'bg-[hsl(var(--step-connector))]',
                    )}
                  />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
